import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Skeleton,
  Stack,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC } from 'react';

import {
  PieceAnnotation,
  TrackingNumberAnnouncementStatus,
  TrackingNumberValidationStatus,
  Workflow,
} from '@apiContract';
import { BarcodesStatusesCounts } from 'src/utils/annotations';

import { endAdornmentContainerSx, sxChip, sxTrackingNumber } from '../styles';
import { getTooltip } from '../utils';

const error = {
  badFormat: 'Tracking number has improper format',
  duplicate: 'Tracking number is associated with an existing active item',
};

const getItemError = (barcodeStatuses: BarcodesStatusesCounts, item?: PieceAnnotation,) => {
  if (
    !item?.pieceAnnotationsTrackingNumber ||
    item.pieceAnnotationsTrackingNumber.validationStatus === TrackingNumberValidationStatus.Valid ||
    item.pieceAnnotationsTrackingNumber.validationStatus === TrackingNumberValidationStatus.None
  )
    return;

  if (
    item.pieceAnnotationsTrackingNumber.validationStatus === TrackingNumberValidationStatus.Conflict &&
    !barcodeStatuses[TrackingNumberValidationStatus.Valid]
  ) {
    return {
      message: error.duplicate,
      color: 'error.main',
    };
  }
  return {
    message:
      item.pieceAnnotationsTrackingNumber.validationStatus === TrackingNumberValidationStatus.BadFormat
        ? error.badFormat
        : error.duplicate,
    color: barcodeStatuses[TrackingNumberValidationStatus.Valid] ? 'text.secondary' : 'warning.main',
  };
};

const Empty = (
  <InputAdornment position="start">
    <Skeleton sx={{ width: 117, height: 14, borderRadius: 0.5 }} />
  </InputAdornment>
);

const Circle = (
  <Box display="flex" justifyContent="center">
    <CircularProgress
      size={22}
      sx={(theme: Theme) => ({
        color: theme.palette.grey[500],
      })}
    />
  </Box>
);

type Props = {
  item?: PieceAnnotation;
  workflow: Workflow;
  barcodeStatuses: BarcodesStatusesCounts;
  hasRecipient: boolean;
};

const TrackInput: FC<Props> = ({ barcodeStatuses, item, workflow, hasRecipient }) => {
  const startAdornment = item && item.textValue ? null : Empty;
  const announcementStatus = item?.pieceAnnotationsTrackingNumber?.announcementStatus;
  const validationStatus = item?.pieceAnnotationsTrackingNumber?.validationStatus;

  const shouldShowSpinner =
    workflow === Workflow.Lockers &&
    hasRecipient &&
    validationStatus === TrackingNumberValidationStatus.Valid &&
    (announcementStatus === TrackingNumberAnnouncementStatus.None ||
      announcementStatus === TrackingNumberAnnouncementStatus.Sent);
  const endAdornment = shouldShowSpinner ? Circle : null;
  const tooltip = getTooltip(workflow, item, hasRecipient) || null;
  const errorText = getItemError(barcodeStatuses, item);
  const carrierName = item?.pieceAnnotationsTrackingNumber?.shippingCarrier?.name;

  return (
    <Tooltip title={tooltip}>
      <Box sx={sxTrackingNumber}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="column">
            <Stack direction="row">
              {startAdornment}
              <Typography variant="body2">{item?.textValue || ''}</Typography>
            </Stack>
            {!!errorText && (
              <Typography color={errorText.color} variant="caption">
                {errorText.message}
              </Typography>
            )}
          </Stack>
          <Box sx={endAdornmentContainerSx}>
            {endAdornment}

            {carrierName && (
              <Stack direction="column" sx={{ ml: 2 }}>
                <Chip label={carrierName} size="small" variant="soft" color="default" sx={sxChip} />
              </Stack>
            )}
          </Box>
        </Stack>
      </Box>
    </Tooltip>
  );
};

export default TrackInput;
